import {
  companyTypesSchema,
  formatIsoDate,
  isGovernmentCompany,
  isNotPastDate,
  normalizeLocalFax,
  normalizePhone,
  requiredAllowedTextMax,
  requiredDate,
  requiredDigitsString,
  requiredDocumentArray,
  requiredEmail,
  requiredLocalFax,
  requiredPhone,
  requiredString,
  requiredStringMax,
  toDate,
} from "@features/licensing/schema/licenseRequestValidation";
import {
  DocumentTypes,
  type CompanyType,
  type VendorLicenseRequestPayload,
} from "@services/licensing/types";
import type { TFunction } from "i18next";
import { z } from "zod";
import { VENDOR_LICENSE_REQUEST_FIELDS as F, tradeLicenseIssuingAuthorities } from "../constants";

export { normalizeLocalFax, normalizePhone, toDate };

export type UploadedDocument = {
  name: string;
  url: string;
  type: string;
};

const documentSchema = z.object({
  name: z.string(),
  url: z.string().min(1),
  type: z.string(),
});

export const getGovernmentVendorLicenseRequestFormSchema = (
  t: TFunction,
  companyTypes: ReadonlyArray<CompanyType> = [],
) => {
  const schema = getVendorLicenseRequestFormSchema(t, companyTypes);
  return schema.extend({
    [F.TRADING_LICENSE_NUMBER]: schema.shape[F.TRADING_LICENSE_NUMBER]
      .or(z.literal(""))
      .optional()
      .default(""),
    [F.TRADING_LICENSE_VALIDITY_DATE]: schema.shape[F.TRADING_LICENSE_VALIDITY_DATE]
      .or(z.literal(""))
      .nullish()
      .transform((value) => value ?? ""),
    [F.TRADE_LICENSE_ISSUING_AUTHORITY]: schema.shape[F.TRADE_LICENSE_ISSUING_AUTHORITY]
      .or(z.literal(""))
      .optional()
      .default(""),
    [F.TRADE_LICENSE_DOCUMENT_URL]: z.array(documentSchema).optional().default([]),
  });
};

const tradeLicenseIssuingAuthoritySchema = (label: string, t: TFunction) =>
  requiredString(label, t).refine(
    (value) => tradeLicenseIssuingAuthorities.some((authority) => authority.value === value),
    {
      message: t("licensing.form-validation.invalid-option", { label }),
    },
  );

export const getVendorLicenseRequestFormSchema = (
  t: TFunction,
  companyTypes: ReadonlyArray<{ companyTypeCode: string; companyTypeDesc: string }> = [],
) =>
  z.object({
    [F.COMPANY_NAME]: requiredAllowedTextMax(
      150,
      t("licensing.vendor-license-request-company-name"),
      t,
    ),

    [F.COMPANY_NAME_ARABIC]: requiredStringMax(
      150,
      t("licensing.vendor-license-request-company-name-arabic"),
      t,
    ),

    [F.COMPANY_TYPES]: companyTypesSchema(
      t("licensing.vendor-license-request-company-type"),
      t,
      companyTypes,
    ),

    [F.COMPANY_ADDRESS]: requiredAllowedTextMax(
      250,
      t("licensing.vendor-license-request-company-address"),
      t,
    ),

    [F.TELEPHONE_NUMBER]: requiredPhone(t("licensing.vendor-license-request-telephone-no"), t),
    [F.MOBILE_NUMBER]: requiredPhone(t("licensing.vendor-license-request-mobile-number"), t),
    [F.FAX_NUMBER]: requiredLocalFax(t("licensing.vendor-license-request-fax-number"), t),

    [F.COMPANY_EMAIL]: requiredEmail(t("licensing.vendor-license-request-company-email"), t),
    [F.APPLICANT_EMAIL]: requiredEmail(t("licensing.vendor-license-request-email-address"), t),

    [F.NATURE_OF_BUSINESS_IN_POF]: requiredAllowedTextMax(
      150,
      t("licensing.vendor-license-request-nature-of-business"),
      t,
    ),

    [F.NUMBER_OF_EMPLOYEES]: requiredDigitsString(
      3,
      t("licensing.vendor-license-request-no-of-employees"),
      t,
    ),
    [F.NUMBER_OF_VEHICLES]: requiredDigitsString(
      3,
      t("licensing.vendor-license-request-no-of-vehicles"),
      t,
    ),

    [F.TRADING_LICENSE_NUMBER]: requiredDigitsString(
      10,
      t("licensing.vendor-license-request-trade-license-number"),
      t,
    ),

    [F.TRADING_LICENSE_VALIDITY_DATE]: requiredDate(
      t("licensing.vendor-license-request-trade-license-validity-date"),
      t,
    ).refine((v) => isNotPastDate(v), {
      message: t("licensing.form-validation.date-not-in-past", {
        label: t("licensing.vendor-license-request-trade-license-validity-date"),
      }),
    }),

    [F.TRADE_LICENSE_ISSUING_AUTHORITY]: tradeLicenseIssuingAuthoritySchema(
      t("licensing.vendor-license-request-trade-license-issuing-authority"),
      t,
    ),

    [F.APPLICANT_NAME]: requiredAllowedTextMax(
      150,
      t("licensing.vendor-license-request-name-of-applicant"),
      t,
    ),
    [F.APPLICANT_POSITION]: requiredAllowedTextMax(
      100,
      t("licensing.vendor-license-request-applicant-position"),
      t,
    ),

    [F.PURPOSE_OF_REQUEST]: requiredAllowedTextMax(
      500,
      t("licensing.vendor-license-request-purpose-of-request"),
      t,
    ),

    [F.TRADE_LICENSE_DOCUMENT_URL]: requiredDocumentArray(
      documentSchema,
      t("licensing.vendor-license-request-trade-license-doc-title"),
      t,
    ),
    [F.COMPANY_INCORPORATION_DOCUMENT_URL]: requiredDocumentArray(
      documentSchema,
      t("licensing.vendor-license-request-company-incorporation-doc-title"),
      t,
    ),
  });

export type VendorLicenseRequestFormData = z.infer<
  ReturnType<typeof getVendorLicenseRequestFormSchema>
>;

export const vendorLicenseRequestDefaultValues: VendorLicenseRequestFormData = {
  companyName: "",
  companyNameArabic: "",
  companyTypes: [],

  companyAddress: "",
  telephoneNumber: "",
  mobileNumber: "",
  faxNumber: "",

  companyEmail: "",
  applicantEmail: "",
  applicantName: "",
  applicantPosition: "",

  natureOfBusinessInPof: "",

  numberOfEmployees: "",
  numberOfVehicles: "",

  tradingLicenseNumber: "",
  tradingLicenseValidityDate: "",

  tradeLicenseIssuingAuthority: "",
  purposeOfRequest: "",

  tradeLicenseDocumentUrl: [],
  companyIncorporationDocumentUrl: [],
};

export const mapVendorLicenseRequestToPayload = (
  data: VendorLicenseRequestFormData,
  companyTypes: ReadonlyArray<CompanyType> = [],
): VendorLicenseRequestPayload => {
  const d = toDate(data.tradingLicenseValidityDate);

  const trade = data.tradeLicenseDocumentUrl?.[0];
  const incorp = data.companyIncorporationDocumentUrl?.[0];

  return {
    companyName: data.companyName,
    companyNameArabic: data.companyNameArabic,
    companyTypes: data.companyTypes.map(
      (companyTypeCode) =>
        companyTypes.find((companyType) => companyType.companyTypeCode === companyTypeCode) ?? {
          companyTypeCode,
          companyTypeDesc: companyTypeCode,
        },
    ),
    companyAddress: data.companyAddress,

    telephoneNumber: normalizePhone(data.telephoneNumber),
    mobileNumber: normalizePhone(data.mobileNumber),
    faxNumber: normalizeLocalFax(data.faxNumber),

    companyEmail: data.companyEmail,

    applicantEmail: data.applicantEmail,
    applicantName: data.applicantName,
    applicantPosition: data.applicantPosition,

    natureOfBusinessInPof: data.natureOfBusinessInPof,

    numberOfEmployees: Number(data.numberOfEmployees),
    numberOfVehicles: Number(data.numberOfVehicles),

    tradingLicenseNumber: data.tradingLicenseNumber || null,
    tradingLicenseValidityDate: d ? formatIsoDate(d) : String(data.tradingLicenseValidityDate),

    tradeLicenseIssuingAuthority: data.tradeLicenseIssuingAuthority || null,
    purposeOfRequest: data.purposeOfRequest,

    documents: [
      ...(!trade && isGovernmentCompany(companyTypes, data.companyTypes)
        ? []
        : [
            {
              documentType: DocumentTypes.TRADE_LICENSE,
              documentUrl: trade?.url ?? "",
              originalFileName: trade?.name ?? "",
            },
          ]),
      {
        documentType: DocumentTypes.COMPANY_INCORPORATION,
        documentUrl: incorp?.url ?? "",
        originalFileName: incorp?.name ?? "",
      },
    ],
  };
};
